// Reportes (§ 1.24): lista de reportes y ejecución con resultado agregado.
import type { JSX } from "react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../lib/api.ts";
import type { Row } from "../lib/resource.ts";
import { Button, Card, EmptyState, Loading } from "../components/ui.tsx";

export function Reports(): JSX.Element {
  const reports = useQuery<{ rows: Row[] }>({ queryKey: ["list", "reports"], queryFn: () => api.get("/reports") });
  const [runId, setRunId] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold text-ink">Reportes</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-1">
          {reports.isLoading ? <Loading /> : (reports.data?.rows ?? []).length === 0 ? (
            <EmptyState title="Sin reportes" body="Los reportes semilla aparecerán aquí." />
          ) : (
            <ul className="divide-y divide-line">
              {(reports.data?.rows ?? []).map((r) => (
                <li key={String(r["id"])}>
                  <button className={`w-full text-left px-4 py-3 hover:bg-surface-hover ${runId === r["id"] ? "bg-primary-50" : ""}`} onClick={() => setRunId(String(r["id"]))}>
                    <div className="text-sm font-medium text-ink">{String(r["name"])}</div>
                    <div className="text-xs text-ink-muted">{String(r["module"])}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <div className="lg:col-span-2">
          {runId ? <ReportResult id={runId} /> : (
            <Card className="p-10 text-center text-sm text-ink-muted">Selecciona un reporte para ejecutarlo.</Card>
          )}
        </div>
      </div>
    </div>
  );
}

function ReportResult({ id }: { id: string }): JSX.Element {
  const run = useQuery<{ report: Row; result: unknown; sampledFrom: number }>({
    queryKey: ["report-run", id],
    queryFn: () => api.get(`/reports/${id}/run`),
  });
  if (run.isLoading) return <Loading />;
  if (!run.data) return <Card className="p-5 text-sm text-red-500">No se pudo ejecutar el reporte.</Card>;

  const result = run.data.result;
  const asArray = Array.isArray(result) ? (result as Record<string, unknown>[]) : null;
  const asGroups = !asArray && result && typeof result === "object" && Array.isArray((result as { groups?: unknown }).groups)
    ? (result as { groups: Record<string, unknown>[] }).groups : null;
  const rows = asArray ?? asGroups;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-ink-secondary">{String(run.data.report["name"])}</h3>
        <span className="text-xs text-ink-muted">{run.data.sampledFrom} registros analizados</span>
      </div>
      {rows && rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="bg-canvas">{Object.keys(rows[0] ?? {}).map((k) => <th key={k} className="th">{k}</th>)}</tr></thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>{Object.keys(rows[0] ?? {}).map((k) => <td key={k} className="td">{String(row[k] ?? "")}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <pre className="text-xs bg-canvas rounded p-3 overflow-x-auto text-ink-secondary">{JSON.stringify(result, null, 2)}</pre>
      )}
      <div className="mt-4"><Button small onClick={() => void run.refetch()}>Actualizar</Button></div>
    </Card>
  );
}
